import { ReactElement } from "react";
import { IonButton, IonIcon, IonItem, IonLabel } from "@ionic/react";
import { micOutline } from "ionicons/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TonalPill } from "../../../atoms/Kit";
import { useQuestionLayout } from "../questionLayout";

interface SpeechToTextRowProps {
  isListening: boolean;
  onStart: () => void;
  /** Card layout only: the row's Ionic lines/mode, as each answer type had them. */
  lines?: "full" | "inset" | "none";
  mode?: "ios" | "md";
}

/**
 * "Cliquer pour parler" under an answer. On the questionnaire screen it is a
 * tonal pill (the whole pill starts listening) instead of an underlined row;
 * inline hosts keep the original row.
 */
const SpeechToTextRow = ({ isListening, onStart, lines, mode }: SpeechToTextRowProps): ReactElement => {
  const { t } = useTranslation();
  const layout = useQuestionLayout();

  const icon = isListening ? (
    <IonIcon src="/assets/icon/loading.svg" aria-hidden="true" />
  ) : (
    <IonIcon icon={micOutline} aria-hidden="true" />
  );

  if (layout === "page") {
    return (
      <SpeechPill as="button" type="button" onClick={onStart} aria-busy={isListening}>
        {icon}
        {t("common_click_to_speak")}
      </SpeechPill>
    );
  }

  return (
    <IonItem lines={lines} mode={mode}>
      <IonLabel>
        <p>{t("common_click_to_speak")}</p>
      </IonLabel>

      <IonButton onClick={onStart} slot="end" fill="clear">
        {isListening ? (
          <IonIcon slot="icon-only" src="/assets/icon/loading.svg" />
        ) : (
          <IonIcon slot="icon-only" icon={micOutline} />
        )}
      </IonButton>
    </IonItem>
  );
};

const SpeechPill = styled(TonalPill)`
  margin-top: 4px;
  min-height: 40px;
  padding: 0 16px;
  font-size: 14.5px;
  cursor: pointer;

  /* Beats the question's blanket icon color: the mic matches its label. */
  && ion-icon {
    color: inherit;
  }

  /* Inside the answer card of text / number / dropdown questions. */
  .question-answer-panel & {
    margin: 10px 0 4px;
  }
`;

export default SpeechToTextRow;
